import { initializeApp } from 'firebase/app';
import 'firebase/auth';
import * as auth from 'firebase/auth';
import { SAMLAuthProvider, signInWithPopup, User } from 'firebase/auth';
import i18n from '../../../i18n';
import { EnvironmentService } from '../environment/environment-service';
import { UserTracker } from '../v2/analytics/trackers/userTracker';
import { UnauthErrorTracker } from '../v2/common/components/errors/trackers/UnauthErrorTracker';
import { Domains } from '../v2/constants';
import { SuspenseResource, wrapPromise } from '../v2/utils';

export enum FirebaseAuthErrorCodes {
  WRONG_PASSWORD = 'auth/wrong-password',
  POPUP_CLOSED_BY_USER = 'auth/popup-closed-by-user',
  USER_NOT_FOUND_FOR_EMAIL = 'auth/user-not-found',
}

export const firebaseErrorCodeToTranslatedDescription: Record<
  FirebaseAuthErrorCodes,
  string
> = {
  [FirebaseAuthErrorCodes.WRONG_PASSWORD]: i18n.t(
    'common:authErrorCodesDescription.wrongPassword'
  ),
  [FirebaseAuthErrorCodes.POPUP_CLOSED_BY_USER]: i18n.t(
    'common:authErrorCodesDescription.popupClosedByUser'
  ),
  [FirebaseAuthErrorCodes.USER_NOT_FOUND_FOR_EMAIL]: i18n.t(
    'common:authErrorCodesDescription.userNotFoundForEmail'
  ),
};

export class AuthenticationService {
  public currentUser: User | null = null;
  public auth: auth.Auth;
  public isPerformingLogout = false;
  public authUserResource: SuspenseResource<User | null>;

  constructor(
    readonly environmentService: EnvironmentService,
    readonly errorTracker: UnauthErrorTracker,
    readonly userTracker: UserTracker
  ) {
    const firebaseConfig = environmentService.getFirebaseConfig();
    if (firebaseConfig.type === 'emulator') {
      const firebaseApp = initializeApp({
        projectId: 'panacloud-local',
        apiKey: 'fake',
      });
      this.auth = auth.getAuth(firebaseApp);
      auth.connectAuthEmulator(this.auth, firebaseConfig.host);
    } else {
      const firebaseApp = initializeApp(firebaseConfig);
      this.auth = auth.getAuth(firebaseApp);
    }

    void this.auth.setPersistence(this.getSessionPersistence());

    // Initialize currentUser
    this.currentUser = this.auth.currentUser;

    auth.onAuthStateChanged(this.auth, (user) => {
      this.currentUser = user;
    });

    let unsubscribe: auth.Unsubscribe | undefined;
    this.authUserResource = wrapPromise(
      new Promise<User | null>((resolve, reject) => {
        unsubscribe = this.auth.onAuthStateChanged(
          (user) => resolve(user),
          (err) => reject(err)
        );
      }).finally(() => {
        unsubscribe?.();
      })
    );
  }

  private handleFirebaseError = async <T>(action: Promise<T>) => {
    return action.catch((e) => {
      const errorMessage =
        firebaseErrorCodeToTranslatedDescription[
          e.code as FirebaseAuthErrorCodes
        ];
      if (!errorMessage) {
        console.error(`Unhandled error code - ${e.code} found`);
      }
      const authError = new Error(errorMessage ?? e.code);
      throw authError;
    });
  };

  public async loginWithPassword(email: string, password: string) {
    return this.handleFirebaseError(
      this.auth
        .setPersistence(this.getSessionPersistence())
        .then(async () => {
          const userCredential = await auth.signInWithEmailAndPassword(
            this.auth,
            email,
            password
          );
          userCredential.user &&
            this.userTracker.trackFirebaseLogin(userCredential.user, { email });
          return userCredential;
        })
        .catch((error) => {
          this.errorTracker.trackAuthenticationError(
            error,
            'login-password',
            null,
            { email }
          );
          throw error;
        })
    );
  }

  public async logInUserWithGoogle() {
    const provider = new auth.GoogleAuthProvider();
    return this.handleFirebaseError(
      this.auth
        .setPersistence(this.getSessionPersistence())
        .then(async () => {
          const userCredential = await auth.signInWithPopup(
            this.auth,
            provider
          );
          this.userTracker.trackFirebaseLogin(userCredential.user, {
            email: userCredential.user.email ?? 'UNKNOWN',
          });
          return userCredential;
        })
        .catch((error) => {
          this.errorTracker.trackAuthenticationError(error, 'login-google-sso');
          throw error;
        })
    );
  }

  public async logInUserWithMicrosoft() {
    const provider = new auth.OAuthProvider('microsoft.com');
    return this.handleFirebaseError(
      this.auth
        .setPersistence(this.getSessionPersistence())
        .then(async () => {
          const userCredential = await auth.signInWithPopup(
            this.auth,
            provider
          );
          this.userTracker.trackFirebaseLogin(userCredential.user, {
            email: userCredential.user.email ?? 'UNKNOWN',
          });
          return userCredential;
        })
        .catch((error) => {
          this.errorTracker.trackAuthenticationError(
            error,
            'login-microsoft-sso'
          );
          throw error;
        })
    );
  }

  public async logInUserWithSAML() {
    this.auth.tenantId = this.environmentService.getSamlTenantId() ?? null;
    const provider = new SAMLAuthProvider(
      this.environmentService.getSamlProviderId()
    );
    return this.handleFirebaseError(
      this.auth
        .setPersistence(this.getSessionPersistence())
        .then(async () => {
          const userCredential = await signInWithPopup(this.auth, provider);
          userCredential.user &&
            this.userTracker.trackFirebaseLogin(userCredential.user, {
              email: userCredential.user.email ?? 'UNKNOWN',
            });
          return userCredential;
        })
        .catch((error) => {
          this.errorTracker.trackAuthenticationError(error, 'login-SAML');
          throw error;
        })
    );
  }

  public async logOutUser(domain: Domains | null) {
    try {
      if (this.isPerformingLogout) {
        return;
      }
      this.isPerformingLogout = true;
      this.currentUser &&
        domain &&
        this.userTracker.trackFirebaseLogout(this.currentUser, domain);
      sessionStorage.clear();
      await this.handleFirebaseError(this.auth.signOut());
      document.location.replace('/login');
    } catch (error) {
      console.error("Couldn't log user out", error);
      throw error;
    } finally {
      this.isPerformingLogout = false;
    }
  }

  public isLoggedIn() {
    return !!this.currentUser;
  }

  public async getAccessToken(forceRefresh?: boolean) {
    return this.currentUser?.getIdToken(Boolean(forceRefresh));
  }

  public async createUserWithEmailAndPassword(email: string, password: string) {
    return this.handleFirebaseError(
      auth.createUserWithEmailAndPassword(this.auth, email, password)
    );
  }

  public async updatePassword(
    email: string,
    currentPassword: string,
    newPassword: string
  ) {
    const credential = auth.EmailAuthProvider.credential(
      email,
      currentPassword
    );
    const user = this.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }
    return this.handleFirebaseError(
      auth
        .reauthenticateWithCredential(user, credential)
        .then(() => auth.updatePassword(user, newPassword))
        .catch((error) => {
          this.errorTracker.trackAuthenticationError(
            error,
            'password-update',
            null,
            { email }
          );
          throw error;
        })
    );
  }

  public async applyActionCode(actionCode: string) {
    return this.handleFirebaseError(
      auth.applyActionCode(this.auth, actionCode).catch((error) => {
        this.errorTracker.trackAuthenticationError(
          error,
          'verify-verification-code',
          null,
          {
            email: this.currentUser?.email,
          }
        );
        throw error;
      })
    );
  }

  private getSessionPersistence: () => auth.Persistence = () => {
    if (this.environmentService.isTest) {
      return auth.inMemoryPersistence;
    }
    if (this.environmentService.isLocalhost) {
      return auth.browserLocalPersistence;
    } else {
      return auth.browserSessionPersistence;
    }
  };

  public async verifyPasswordResetCode(code: string) {
    return this.handleFirebaseError(
      auth.verifyPasswordResetCode(this.auth, code)
    );
  }

  public async confirmPasswordReset(
    code: string,
    email: string,
    newPassword: string
  ) {
    return this.handleFirebaseError(
      auth.confirmPasswordReset(this.auth, code, newPassword).catch((error) => {
        this.errorTracker.trackAuthenticationError(
          error,
          'password-reset',
          null,
          { email }
        );
        throw error;
      })
    );
  }

  public async linkWithCredential(
    user: auth.User,
    credential: auth.AuthCredential
  ) {
    return this.handleFirebaseError(auth.linkWithCredential(user, credential));
  }

  public get isSaml(): boolean {
    return (
      !!this.environmentService.getSamlTenantId() &&
      !!this.currentUser?.tenantId &&
      this.environmentService.getSamlTenantId() === this.currentUser?.tenantId
    );
  }
}
